import React from 'react';
import { fmtPct } from '@/lib/calculations';
import { RotateCcw, Edit3, Check } from 'lucide-react';

export function AdminSutaRatesCard({
  STATES,
  sutaRates,
  sutaRatesDraft,
  sutaRatesSaved,
  startEditSutaRates,
  resetSutaRatesToDefaults,
  saveSutaRates,
  cancelEditSutaRates,
  updateDraftRate,
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="suta-rates-card">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-base font-semibold font-['Outfit'] text-slate-900">
          SUTA Rate Table (Bill / Cost)
        </h2>
        <div className="flex items-center gap-2">
          {sutaRatesSaved && (
            <span className="text-xs text-emerald-600 font-medium" data-testid="suta-rates-saved-msg">
              {sutaRatesSaved}
            </span>
          )}
          {!sutaRatesDraft ? (
            <>
              <button
                data-testid="edit-suta-rates-btn"
                onClick={startEditSutaRates}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#3c68ff] text-white rounded-lg text-xs font-medium hover:bg-[#2b4bca] transition-all"
              >
                <Edit3 size={13} /> Edit Rates
              </button>
              <button
                data-testid="reset-suta-rates-btn"
                onClick={resetSutaRatesToDefaults}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-all"
              >
                <RotateCcw size={13} /> Reset
              </button>
            </>
          ) : (
            <>
              <button
                data-testid="save-suta-rates-btn"
                onClick={saveSutaRates}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 transition-all"
              >
                <Check size={13} /> Save
              </button>
              <button
                data-testid="cancel-suta-rates-btn"
                onClick={cancelEditSutaRates}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-4 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider w-16">
                State
              </th>
              <th className="px-4 py-2.5 text-right font-semibold text-slate-500 uppercase tracking-wider">
                Bill Rate %
              </th>
              <th className="px-4 py-2.5 text-right font-semibold text-slate-500 uppercase tracking-wider">
                Cost Rate %
              </th>
              <th className="px-4 py-2.5 text-left font-semibold text-slate-500 uppercase tracking-wider">
                Type
              </th>
            </tr>
          </thead>
          <tbody>
            {STATES.map(st => {
              const live = sutaRates[st] || { bill: null, cost: null };
              const d = sutaRatesDraft?.[st] || live;
              const isClient = live.bill === null;
              return (
                <tr key={st} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-2 font-semibold text-slate-900">{st}</td>
                  <td className="px-4 py-2 text-right">
                    {sutaRatesDraft ? (
                      <input
                        data-testid={`suta-bill-${st}`}
                        value={d.bill === null ? "" : d.bill}
                        onChange={e => updateDraftRate(st, "bill", e.target.value)}
                        placeholder="CLIENT"
                        className="w-20 h-6 rounded border border-slate-200 px-2 text-right font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#3c68ff]"
                      />
                    ) : (
                      <span className={`font-mono ${isClient ? "text-amber-600 font-semibold" : ""}`}>
                        {isClient ? "CLIENT" : fmtPct(live.bill)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {sutaRatesDraft ? (
                      <input
                        data-testid={`suta-cost-${st}`}
                        value={d.cost === null ? "" : d.cost}
                        onChange={e => updateDraftRate(st, "cost", e.target.value)}
                        placeholder="CLIENT"
                        className="w-20 h-6 rounded border border-slate-200 px-2 text-right font-mono text-xs focus:outline-none focus:ring-1 focus:ring-[#3c68ff]"
                      />
                    ) : (
                      <span className={`font-mono ${isClient ? "text-amber-600 font-semibold" : ""}`}>
                        {isClient ? "CLIENT" : fmtPct(live.cost)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {isClient ? (
                      <span className="text-amber-600 font-semibold text-[10px] uppercase tracking-wider">
                        Pass-through
                      </span>
                    ) : (
                      "Standard"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
